import React, { useCallback } from "react";
import { TID } from "../constants/testIds";
import { useLanguage } from "../context/LanguageContext";

/**
 * Skip Link — an accessibility affordance for keyboard and screen-reader
 * users. It is visually hidden until it receives keyboard focus, at
 * which point it appears as an editorial pill in the top-left of the
 * viewport. Activation jumps focus to the first <main> element on the
 * page (or the first <h1> as a fallback) so assistive tech users can
 * bypass the header and language toggle on every route.
 *
 * Preserves the calm editorial identity: monospace overline styling,
 * ocean accent underline, no bright colours.
 */
export default function SkipLink() {
    const { lang } = useLanguage();
    const label = lang === "nl" ? "Ga direct naar de inhoud" : "Skip to main content";

    const handleSkip = useCallback((e) => {
        e.preventDefault();
        const target =
            document.querySelector("main") ||
            document.querySelector("h1") ||
            document.getElementById("main");
        if (!target) return;
        // Make the target focusable if it isn't naturally
        const prev = target.getAttribute("tabindex");
        if (prev === null) target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: false });
        target.scrollIntoView({ block: "start", behavior: "auto" });
        // Restore tabindex after blur so we don't leave orphaned attributes
        const restore = () => {
            if (prev === null) target.removeAttribute("tabindex");
            target.removeEventListener("blur", restore);
        };
        target.addEventListener("blur", restore);
    }, []);

    return (
        <a
            href="#main"
            data-testid={TID.skipLink}
            onClick={handleSkip}
            className="skip-link"
        >
            {label}
        </a>
    );
}
